import {DEFAULT_INTENT_PRELOAD_DELAY} from '@github-ui/react-router'
import type React from 'react'
import {useEffect, useId, useRef} from 'react'

const INTENT_GROUP_ATTRIBUTE = 'data-intent-group'

/**
 * Returns props for one option that call `onIntent` once the pointer or keyboard focus has
 * stayed on it for `DEFAULT_INTENT_PRELOAD_DELAY`, the delay intent-preloading links use. Each
 * option calls it once until the pointer or focus leaves, and `cancel`, which a pick calls,
 * stops one still waiting.
 *
 * Pointer intent starts on movement, so a menu opening under a resting pointer starts nothing.
 * Focus counts only when it moves from another option of the same control: focus a menu puts
 * on its first option when it opens is not a choice.
 */
export function useOptionIntent<TValue>(onIntent: ((value: TValue) => void) | undefined) {
  const groupId = useId()
  const pendingRef = useRef<{value: TValue; timer: ReturnType<typeof setTimeout> | null} | null>(null)
  const onIntentRef = useRef(onIntent)
  useEffect(() => {
    onIntentRef.current = onIntent
  })

  const cancel = () => {
    if (pendingRef.current?.timer) clearTimeout(pendingRef.current.timer)
    pendingRef.current = null
  }
  useEffect(
    () => () => {
      if (pendingRef.current?.timer) clearTimeout(pendingRef.current.timer)
    },
    [],
  )

  /** Props for the option with `value`. Every option carries the group, and only `enabled` ones listen. */
  const propsFor = (value: TValue, enabled: boolean) => {
    const group = {[INTENT_GROUP_ATTRIBUTE]: groupId}
    if (!onIntent || !enabled) return group
    const start = () => {
      if (pendingRef.current?.value === value) return
      cancel()
      const pending: {value: TValue; timer: ReturnType<typeof setTimeout> | null} = {value, timer: null}
      pending.timer = setTimeout(() => {
        pending.timer = null
        onIntentRef.current?.(value)
      }, DEFAULT_INTENT_PRELOAD_DELAY)
      pendingRef.current = pending
    }
    const leave = () => {
      if (pendingRef.current?.value === value) cancel()
    }
    return {
      ...group,
      onPointerMove: start,
      onPointerLeave: leave,
      onFocus: (event: React.FocusEvent) => {
        const from =
          event.relatedTarget instanceof Element ? event.relatedTarget.closest(`[${INTENT_GROUP_ATTRIBUTE}]`) : null
        if (from?.getAttribute(INTENT_GROUP_ATTRIBUTE) === groupId) start()
      },
      onBlur: leave,
    }
  }

  return {propsFor, cancel}
}
